import React, { useState, useEffect, useCallback, useRef } from "react";
import { useAuth } from "../../contexts/AuthContext";
import "./Enquiries.scss";
import axiosInstance from "../../axiosInstance";
import { toast } from "react-toastify";
import EnquiryStageHistory from "../../components/EnquiryStageHistory/EnquiryStageHistory";
import WebSocketService from "../../components/WebSockets/WebSocketService";
import LeadQuickAddModal from "./LeadQuickAddModal";
import MinutesOfMeetingManager from "../../components/EnquiryStageHistory/MinutesOfMeetingManager";
import AddProductsModal from "./AddProductsModal";
import LoadingScreen from "../../components/LoadingScreen/LoadingScreen";
import { useLocation } from "react-router-dom";
import EnquiryCreateForm from "./EnquiryCreateForm";
import {
  STAGE_HISTORY_PAGE_SIZE,
  groupLightweightStageHistory,
  appendLightweightStageHistory,
  replaceCardInStageHistory,
  calculateSellingPrice,
} from "./enquiryStageHelpers";

const Enquiries = () => {
  const { user } = useAuth();
  const [stageHistory, setStageHistory] = useState([]);
  // Lightweight per-stage totals (accurate totalEnquiries/totalQuotationAmount
  // across ALL of a stage's enquiries, not just the loaded page) from
  // `/enquiry-stage-history/employee/summary`. Fetched first so kanban
  // column headers can populate immediately; the slower paginated detail
  // fetch below still drives the actual card list per column.
  const [stageSummary, setStageSummary] = useState([]);
  const [enquiryStages, setEnquiryStages] = useState([]);
  const [leads, setLeads] = useState([]);
  const [contactPersons, setContactPersons] = useState([]);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [clients, setClients] = useState([]);
  const [editMode, setEditMode] = useState(false);
  const [editingEnquiryId, setEditingEnquiryId] = useState(null);
  const [showQuickAddLead, setShowQuickAddLead] = useState(false);
  const [isWebSocketConnected, setIsWebSocketConnected] = useState(false);
  const productCatalogRef = useRef(null);
  const debounceTimerRef = useRef(null);
  // Pagination is now per-stage/per-column, since the backend requires a
  // `stageId` and paginates that stage's records independently. The API
  // itself now takes `page` (1-based: 1, 2, 3...) and `size` instead of
  // fromRecord/toRecord. Keyed by enquiryStageId:
  //   paginationByStage[stageId] = { nextPage, hasMore }
  //   loadingStageIds = Set of stageIds currently fetching their next page
  const [paginationByStage, setPaginationByStage] = useState({});
  const [loadingStageIds, setLoadingStageIds] = useState(() => new Set());
  const loadingStageIdsRef = useRef(new Set());
  // Mirrors `enquiryStages` for use inside fetchStageHistory without adding
  // it as a useCallback dependency (that would re-run the mount effect below
  // — which reconnects the websocket — every time stages load/change).
  const enquiryStagesRef = useRef([]);
  const [momModal, setMomModal] = useState({
    isOpen: false,
    enquiryId: null,
    enquiryStageHistoryId: null,
  });

  const location = useLocation();

  const [addProductsModal, setAddProductsModal] = useState({
    isOpen: false,
    enquiryStageHistoryId: null,
    enquiryId: null,
    currentProducts: [],
  });

  // New state for product combinations
  const [allProductCombinations, setAllProductCombinations] = useState([]);
  const [categories, setCategories] = useState([]);
  // Holds the chain of selected category ids from the root down to whatever
  // depth the user has drilled into (e.g. [34, 35, 36] for
  // Category -> SubCategory -> SubSubCategory). Categories can nest to any
  // depth, so a simple category/subCategory pair isn't enough.
  const [selectedCategoryPath, setSelectedCategoryPath] = useState([]);

  // Given the full categories tree and the current selection path, returns
  // one array of category options per visible level (level 0 = top
  // categories, level 1 = children of the selected top category, etc.).
  // Stops as soon as a level has no children or nothing is selected there.
  const getCategoryLevels = useCallback(() => {
    const levels = [];
    let currentList = categories;

    for (let depth = 0; depth <= selectedCategoryPath.length; depth++) {
      if (!currentList || currentList.length === 0) break;
      levels.push(currentList);

      const selectedId = selectedCategoryPath[depth];
      if (selectedId === undefined) break;

      const selectedNode = currentList.find((c) => c.id === selectedId);
      if (!selectedNode) break;

      currentList = selectedNode.subCategories;
    }

    return levels;
  }, [categories, selectedCategoryPath]);

  const deepestSelectedCategoryId =
    selectedCategoryPath.length > 0
      ? selectedCategoryPath[selectedCategoryPath.length - 1]
      : null;

  // ── NOTE + IMAGE PAIRS (PDF-only, not saved to backend as structured data) ─
  // Each pair: { id: string, note: string, imageFile: File|null, imageDataUrl: string|null }
  const [noteImagePairs, setNoteImagePairs] = useState([]);

  // Form states - Updated structure
  const [enquiryForm, setEnquiryForm] = useState({
    id: 0,
    leadId: 0,
    clientId: 0,
    contactPersonId: 0,
    clientCompanyName: "",
    priority: "HIGH",
    expectedCloseDate: new Date().toISOString().split("T")[0],
    discountPercentage: "",
    notes: "",
    type: "HOT",
    status: "PENDING",
    currentEnquiryStageHistoryId: null,
    products: [],
  });

  // Search state
  const [searchQuery, setSearchQuery] = useState("");

  const getApiEndpoint = useCallback(
    (baseEndpoint) => {
      const role = user?.role?.toLowerCase();
      if (role === "sales_manager" || role === "salesmanager") {
        return `${process.env.REACT_APP_SALES_MANAGER}${baseEndpoint}`;
      }
      return `${process.env.REACT_APP_SALES}${baseEndpoint}`;
    },
    [user?.role],
  );

  // Fetches the lightweight per-stage summary — total enquiry count and
  // total quotation amount for every stage in one call, independent of
  // pagination — and uses it to populate each kanban column's header stats
  // right away. This is triggered first (see fetchStageHistory below), well
  // ahead of the slower per-stage paginated detail fetch that fills in the
  // actual cards.
  const fetchStageSummary = useCallback(async () => {
    try {
      const response = await axiosInstance.get(
        getApiEndpoint("/enquiry-stage-history/employee/summary"),
      );
      setStageSummary(response.data?.data || []);
    } catch (err) {
      console.error("Failed to fetch enquiry stage summary:", err);
    }
  }, [getApiEndpoint]);

  // Fetches the first page for every stage/column in parallel and rebuilds
  // stageHistory from scratch. Needs the stage list first (each stage's
  // `enquiryStageId` is now a required query param), so this reads
  // enquiryStagesRef.current rather than taking stages as an argument —
  // callers should make sure stages are loaded before calling this (see the
  // mount effect below).
  const fetchStageHistory = useCallback(async () => {
    const stages = enquiryStagesRef.current;
    if (!stages || stages.length === 0) return;

    // Trigger the summary fetch first — it doesn't depend on the paginated
    // detail below and lets kanban column headers populate immediately.
    fetchStageSummary();

    try {
      setLoading(true);

      const results = await Promise.all(
        stages.map((stage) =>
          axiosInstance
            .get(getApiEndpoint("/enquiry-stage-history/employee/details"), {
              params: {
                stageId: stage.enquiryStageId,
                page: 0,
                size: STAGE_HISTORY_PAGE_SIZE,
              },
            })
            .then((response) => ({
              stageId: stage.enquiryStageId,
              records: response.data?.data || [],
            }))
            .catch((err) => {
              console.error(
                `Failed to fetch stage history for stage ${stage.enquiryStageId}:`,
                err,
              );
              return { stageId: stage.enquiryStageId, records: [] };
            }),
        ),
      );

      const allRecords = results.flatMap((result) => result.records);
      setStageHistory(groupLightweightStageHistory(allRecords, stages));

      const nextPaginationByStage = {};
      results.forEach(({ stageId, records }) => {
        nextPaginationByStage[stageId] = {
          // Initial load used page 0, so the next page to fetch on scroll is 1.
          nextPage: 1,
          // A full page means there's likely more behind it; a short page
          // means this stage has nothing further to give us.
          hasMore: records.length === STAGE_HISTORY_PAGE_SIZE,
        };
      });
      setPaginationByStage(nextPaginationByStage);
    } catch (err) {
      console.log(err);
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [getApiEndpoint, fetchStageSummary]);

  // The filter detail endpoints return the same lightweight, flat card shape
  // as the regular board endpoint. Regroup it before storing so every view
  // (kanban, list, table, and calendar) continues to consume one shape.
  const filterRequestIdRef = useRef(0);
  const fetchStageHistoryForFilter = useCallback(
    async ({ type, startDate, clientId, leadId, stageId }) => {
      if (type === "all") {
        filterRequestIdRef.current++;
        fetchStageHistory();
        return;
      }

      const endpointByType = {
        startDate: "/enquiry-stage-history/employee/details/by-start-date",
        client: "/enquiry-stage-history/client/details",
        lead: "/enquiry-stage-history/lead/details",
      };
      const endpoint = endpointByType[type];
      if (!endpoint) return;

      const requestId = ++filterRequestIdRef.current;

      try {
        setLoading(true);

        let records = [];
        if (type === "startDate") {
          const response = await axiosInstance.get(getApiEndpoint(endpoint), {
            params: { startDate },
          });
          records = response.data?.data || [];
        } else {
          // client/lead endpoints require a stageId. With no stage selected,
          // query every stage in parallel and merge.
          const stageIds = stageId
            ? [stageId]
            : (enquiryStagesRef.current || []).map((s) => s.enquiryStageId);

          const results = await Promise.all(
            stageIds.map((id) =>
              axiosInstance
                .get(getApiEndpoint(endpoint), {
                  params:
                    type === "client"
                      ? { clientId, stageId: id }
                      : { leadId, stageId: id },
                })
                .then((response) => response.data?.data || [])
                .catch((err) => {
                  console.error(`Filter fetch failed for stage ${id}:`, err);
                  return [];
                }),
            ),
          );
          records = results.flat();
        }

        // Ignore stale responses if the user changed filters meanwhile.
        if (requestId !== filterRequestIdRef.current) return;

        setStageHistory(
          groupLightweightStageHistory(records, enquiryStagesRef.current),
        );
        // Filter endpoints are unpaginated, and global summary totals would
        // otherwise show counts from the unfiltered board.
        setPaginationByStage({});
        setStageSummary([]);
      } catch (err) {
        console.error("Failed to fetch filtered enquiry history:", err);
        toast.error("Failed to apply enquiry filter");
      } finally {
        setLoading(false);
      }
    },
    [fetchStageHistory, getApiEndpoint],
  );

  // Loads the next page for a single column and merges it into the
  // already-grouped stageHistory (see appendLightweightStageHistory above).
  // This is what a column's own scroll container (inside
  // <EnquiryStageHistory />) should call as the user nears the bottom of
  // that specific column.
  const fetchMoreStageHistoryForStage = useCallback(
    async (stageId) => {
      const stagePagination = paginationByStage[stageId];
      if (!stagePagination?.hasMore) return;
      if (loadingStageIdsRef.current.has(stageId)) return; // already in flight

      loadingStageIdsRef.current.add(stageId);
      setLoadingStageIds(new Set(loadingStageIdsRef.current));

      try {
        const page = stagePagination.nextPage;

        const response = await axiosInstance.get(
          getApiEndpoint("/enquiry-stage-history/employee/details"),
          {
            params: { stageId, page, size: STAGE_HISTORY_PAGE_SIZE },
          },
        );
        const records = response.data?.data || [];

        setStageHistory((prev) =>
          appendLightweightStageHistory(
            prev,
            records,
            enquiryStagesRef.current,
          ),
        );

        setPaginationByStage((prev) => ({
          ...prev,
          [stageId]: {
            nextPage: page + 1,
            hasMore: records.length === STAGE_HISTORY_PAGE_SIZE,
          },
        }));
      } catch (err) {
        console.error(
          `Failed to fetch more stage history for stage ${stageId}:`,
          err,
        );
      } finally {
        loadingStageIdsRef.current.delete(stageId);
        setLoadingStageIds(new Set(loadingStageIdsRef.current));
      }
    },
    [getApiEndpoint, paginationByStage],
  );

  const completeChecklistItem = async (
    checklistHistoryId,
    remarks = "",
    enquiryStageHistoryId = null,
  ) => {
    try {
      const payload = {
        completed: true,
        remarks: remarks || "",
      };

      const response = await axiosInstance.put(
        getApiEndpoint(`/enquiry/checklist-history/${checklistHistoryId}`),
        payload,
      );

      if (response.data.status || response.status === 200) {
        toast.success("Checklist item completed!");

        // Optimistic local patch first, so the tick shows immediately.
        setStageHistory((prev) => {
          return prev.map((stageGroup) => {
            const updatedHistories = stageGroup.histories?.map((history) => {
              if (history.checkListHistories) {
                const updatedChecklists = history.checkListHistories.map(
                  (checklist) => {
                    if (checklist.id === checklistHistoryId) {
                      return {
                        ...checklist,
                        completed: true,
                        remarks: remarks || "",
                      };
                    }
                    return checklist;
                  },
                );

                if (
                  updatedChecklists.some((cl) => cl.id === checklistHistoryId)
                ) {
                  return {
                    ...history,
                    checkListHistories: updatedChecklists,
                    completedTasks: updatedChecklists.filter(
                      (cl) => cl.completed,
                    ).length,
                  };
                }
              }
              return history;
            });

            if (
              updatedHistories?.some((h) =>
                h.checkListHistories?.some(
                  (cl) => cl.id === checklistHistoryId,
                ),
              )
            ) {
              return {
                ...stageGroup,
                histories: updatedHistories,
              };
            }

            return stageGroup;
          });
        });

        // Then reconcile that one card against the server so completion
        // state, counts, and anything else server-computed are accurate —
        // only this single per-id endpoint fires, no board-wide refetch.
        if (enquiryStageHistoryId) {
          try {
            const detailResponse = await axiosInstance.get(
              getApiEndpoint(
                `/enquiry-stage-history/employee/${enquiryStageHistoryId}`,
              ),
            );
            const cardDetail = detailResponse.data?.data;
            setStageHistory((prev) =>
              replaceCardInStageHistory(prev, cardDetail),
            );
          } catch (detailErr) {
            console.error(
              `Failed to refresh card ${enquiryStageHistoryId}:`,
              detailErr,
            );
          }
        }

        return true;
      }
    } catch (err) {
      console.error(err);
      return false;
    }
  };

  // Fetch all categories
  const fetchCategories = useCallback(async () => {
    try {
      const response = await axiosInstance.get(
        getApiEndpoint("/categories/all"),
      );

      if (response.data?.data) {
        setCategories(response.data.data);
      }
    } catch (err) {
      console.error("Failed to fetch categories:", err);
    }
  }, [getApiEndpoint]);

  const fetchClients = useCallback(async () => {
    try {
      const response = await axiosInstance.get(
        getApiEndpoint("/clients/assigned"),
      );

      if (response.data?.data) {
        setClients(response.data.data);
      }
    } catch (err) {
      console.error(err);
    }
  }, [getApiEndpoint]);

  const fetchEnquiryStages = useCallback(async () => {
    try {
      const response = await axiosInstance.get(
        getApiEndpoint("/enquiry-stages"),
      );

      if (response.data?.data) {
        setEnquiryStages(response.data.data);
        enquiryStagesRef.current = response.data.data;
      }
    } catch (err) {
      console.error("Error fetching enquiry stages:", err);
    }
  }, [getApiEndpoint]);

  const fetchLeads = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(
        getApiEndpoint("/leads/assigned-leads"),
      );

      if (response.data?.data) {
        setLeads(response.data.data);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [getApiEndpoint]);

  // UPDATED: Fetch all product combinations with customPricing info
  const fetchAllProductCombinations = useCallback(async () => {
    try {
      let endpoint;

      if (!deepestSelectedCategoryId) {
        endpoint = getApiEndpoint("/products/pricing/combinations/all");
      } else {
        endpoint = getApiEndpoint(
          `/products/pricing/category/${deepestSelectedCategoryId}/combinations`,
        );
      }

      const response = await axiosInstance.get(endpoint);

      if (Array.isArray(response.data?.data)) {
        setAllProductCombinations(response.data.data);
      } else {
        // API can return a 2xx status with an error/conflict body
        // (e.g. { status: 201, error: "Conflict", message: "No products found" })
        // instead of throwing, so treat anything without a data array as empty.
        setAllProductCombinations([]);
      }
    } catch (err) {
      console.error("Failed to fetch product combinations:", err);
      setAllProductCombinations([]);
    }
  }, [getApiEndpoint, deepestSelectedCategoryId]);

  // Fetch lead details
  const fetchLeadDetails = useCallback(
    async (leadId) => {
      if (!leadId) return;

      try {
        const response = await axiosInstance.get(
          getApiEndpoint(`/leads/${leadId}`),
        );

        if (response.data?.data) {
          const leadData = response.data.data;
          setContactPersons(leadData.contactPersons || []);

          const firstCP = leadData.contactPersons?.[0]?.id || 0;

          setEnquiryForm((prev) => ({
            ...prev,
            leadId: leadData.id,
            clientCompanyName: leadData.companyName || "",
            notes: leadData.notes || prev.notes,
            contactPersonId: firstCP,
          }));
        }
      } catch (err) {
        console.error(err);
        setContactPersons([]);
      }
    },
    [getApiEndpoint],
  );

  const handleLeadChange = useCallback(
    (selectedLeadId) => {
      const leadId = selectedLeadId ? Number(selectedLeadId) : 0;

      setEnquiryForm((prev) => ({
        ...prev,
        leadId,
        clientId: 0,
        contactPersonId: 0,
        clientCompanyName: "",
      }));

      if (leadId > 0) {
        fetchLeadDetails(leadId);
      } else {
        setContactPersons([]);
      }
    },
    [fetchLeadDetails],
  );

  useEffect(() => {
    if (location.state?.autoCreateFromLead && location.state?.leadId) {
      const leadId = location.state.leadId;

      setShowCreateForm(true);

      setTimeout(() => {
        handleLeadChange(leadId);
      }, 300);
    }
  }, [location.state, handleLeadChange]);

  useEffect(() => {
    fetchStageSummary();

    (async () => {
      await fetchEnquiryStages();
      fetchStageHistory();
    })();
    fetchLeads();
    fetchClients();
    fetchCategories();

    WebSocketService.connect({
      tenantId: sessionStorage.getItem("tenantId"),
      empId: user.empId,
    });

    const checkConnection = setInterval(() => {
      const isConnected = WebSocketService.isConnected();
      setIsWebSocketConnected(isConnected);
    }, 2000);

    const handleEnquiryUpdate = (data) => {
      console.log("📨 Received enquiry update:", data);

      toast.info("Enquiry updated in real-time", {
        position: "top-right",
        autoClose: 3000,
      });

      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = setTimeout(() => {
        fetchStageHistory();
      }, 1000);
    };

    WebSocketService.subscribeToEnquiryUpdates(
      sessionStorage.getItem("tenantId"),
      user.empId,
      handleEnquiryUpdate,
    );

    return () => {
      console.log("🧹 Cleaning up Enquiries WebSocket subscriptions");
      clearInterval(checkConnection);
      WebSocketService.unsubscribeFromTopic("enquiry");
      WebSocketService.unsubscribeFromTopic("checklist");
    };
  }, [
    user?.tenantId,
    user?.empId,
    fetchStageHistory,
    fetchStageSummary,
    fetchLeads,
    fetchClients,
    fetchEnquiryStages,
    fetchCategories,
  ]);

  useEffect(() => {
    fetchAllProductCombinations();
  }, [fetchAllProductCombinations]);

  const handleLeadCreated = async (newLead) => {
    await fetchLeads();

    if (newLead && newLead.id) {
      handleLeadChange(newLead.id);
      toast.info(`Lead "${newLead.companyName}" selected`);
    }
  };

  // Fetches the full enquiry (lead/client, stage histories with products,
  // etc.) so <EnquiryCreateForm /> can be prefilled when the user chooses
  // "Add Products" from <AddProductsModal />. Response shape confirmed as
  // { status, message, data: { enquiryId, lead, client, stageHistories, ... } }.
  // NOTE: the endpoint path itself is still assumed to mirror the POST
  // /enquiries create endpoint below — adjust if yours differs.
  const fetchEnquiryForEdit = async (enquiryId) => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(
        getApiEndpoint(`/sales-enquiry/${enquiryId}`),
      );
      return response.data?.data || null;
    } catch (err) {
      console.error("Failed to fetch enquiry for editing:", err);
      toast.error("Failed to load enquiry details");
      return null;
    } finally {
      setLoading(false);
    }
  };

  // Fetches each product line's current (editable) price for this enquiry —
  // GET /enquiry/{enquiryId}/product-prices — so the edit form can prefill
  // prices from the source of truth instead of whatever was last saved on
  // the stage-history snapshot. Returns a map of
  // productEnquiryHistoryId -> price for easy merging into mapped products;
  // an empty map on failure just leaves the mapEnquiryToFormState prices as
  // the fallback.
  const fetchProductPrices = async (enquiryId) => {
    try {
      const response = await axiosInstance.get(
        getApiEndpoint(`/enquiry/${enquiryId}/product-prices`),
      );
      const rows = response.data?.data || [];
      const priceByHistoryId = {};
      rows.forEach((row) => {
        if (row.productEnquiryHistoryId != null) {
          priceByHistoryId[row.productEnquiryHistoryId] = row.price;
        }
      });
      return priceByHistoryId;
    } catch (err) {
      console.error("Failed to fetch product prices for editing:", err);
      return {};
    }
  };

  // Maps a full enquiry detail response (see the sample fetched from
  // /enquiries/{id} — { enquiryId, lead, client, stageHistories, ... }) into
  // the enquiryForm shape <EnquiryCreateForm /> expects.
  //
  // Products come from the *current* stage's productEnquiryHistories (an
  // enquiry can have several stageHistories — one per stage it has passed
  // through — and only the current one reflects what's actually still on
  // the enquiry). Each entry gives us resolved spec text
  // (specificationName/specificationValue) but not the
  // productSpecificationValueId the submit payload needs, so we look that
  // up from the already-loaded product catalog via combinationId instead.
  const mapEnquiryToFormState = (enquiry) => {
    const currentStage =
      enquiry.stageHistories?.find(
        (s) => s.stageName === enquiry.currentStageName,
      ) || enquiry.stageHistories?.[enquiry.stageHistories.length - 1];

    const products = (currentStage?.productEnquiryHistories || []).map((p) => {
      const combinationId = p.combinationDetails?.combinationId || null;
      const catalogProduct = allProductCombinations.find(
        (cp) => cp.productId === p.productId,
      );
      const catalogCombination = catalogProduct?.combinations?.find(
        (c) => c.combinationId === combinationId,
      );

      if (!catalogCombination) {
        console.warn(
          `EnquiryCreateForm prefill: couldn't find catalog combination ${combinationId} for product ${p.productId} — its specifications will be empty until re-selected from the catalog.`,
        );
      }

      return {
        // Required by PUT /sales/{enquiryId} so the backend matches this
        // line to its existing product-enquiry-history row instead of
        // creating a duplicate. Verify `p.id` is really the key your
        // /sales-enquiry/{id} response uses here — swap to
        // `p.productEnquiryHistoryId` if that's the actual field name.
        productEnquiryHistoryId: p.id ?? p.productEnquiryHistoryId ?? null,
        productId: p.productId,
        combinationId,
        quantity: p.quantity,
        price: p.price,
        taxRate: p.taxRate ?? 0,
        // The backend already tracks whether this line has a manually
        // overridden price (customPrice non-null) vs the standard catalog
        // price — honor that instead of assuming every prefilled line is
        // custom.
        customPrice: p.customPrice != null,
        status: (p.status || "PENDING").toUpperCase(),
        specifications: catalogCombination
          ? catalogCombination.specificationValues.map((spec) => ({
              productSpecificationId: spec.specificationId,
              productSpecificationValueId: spec.valueId,
            }))
          : [],
        topicsRequestDTOList: [],
        _displayInfo: {
          productName: p.productName,
          specs: (p.specifications || []).map((s) => ({
            specificationName: s.specificationName,
            value: s.specificationValue,
          })),
          sellingPrice: p.price,
          taxRate: p.taxRate ?? 0,
          combinationId,
        },
      };
    });

    const leadId = enquiry.lead?.leadId ?? enquiry.lead?.id ?? 0;
    const clientId =
      enquiry.client?.clientId ?? enquiry.client?.id ?? enquiry.clientId ?? 0;

    if (!leadId && !clientId) {
      console.error(
        "Enquiry prefill: couldn't find a lead or client id on the fetched enquiry. Check that enquiry.lead/.leadId or enquiry.client/.clientId match your API's actual field names.",
        enquiry,
      );
    }

    return {
      id: enquiry.enquiryId,
      leadId,
      clientId,
      // The real response puts this flat on the enquiry itself
      // (enquiry.contactPersonId) — it's not nested under lead/client's
      // contactPerson, which is often null even when a contact person is
      // set on the enquiry (see the sample response: lead.contactPerson is
      // null but enquiry.contactPersonId is 4). Keep the old nested lookups
      // as a fallback only, don't let them take priority.
      contactPersonId:
        enquiry.contactPersonId ||
        enquiry.lead?.contactPerson?.id ||
        enquiry.client?.contactPerson?.id ||
        0,
      clientCompanyName:
        enquiry.lead?.companyName || enquiry.client?.companyName || "",
      priority: (enquiry.priority || "MEDIUM").toUpperCase(),
      expectedCloseDate:
        enquiry.expectedCloseDate || new Date().toISOString().split("T")[0],
      discountPercentage: enquiry.discountPercentage ?? "",
      notes: enquiry.notes || "",
      type: (enquiry.type || "WARM").toUpperCase(),
      status: (enquiry.status || "PENDING").toUpperCase(),
      // The PUT /sales/{enquiryId} payload wants enquiryStageHistoryId on
      // every topic (see createEnquiry) — stash the current stage's id here
      // during prefill so we have it later without re-deriving currentStage.
      // Stage-history objects key this as `enquiryStageHistoryId`, not `id`.
      currentEnquiryStageHistoryId: currentStage?.enquiryStageHistoryId ?? null,
      products,
    };
  };

  // Redirects from <AddProductsModal /> into the full create/edit form,
  // pre-filled with this enquiry's data. Lead/Client stay fixed (disabled in
  // the form) — only Contact Person, Priority, Type, dates, discount, notes
  // and products are actually editable here.
  const handleEditEnquiry = async (enquiryId) => {
    if (!enquiryId) return;

    const enquiry = await fetchEnquiryForEdit(enquiryId);
    if (!enquiry) return;

    const formState = mapEnquiryToFormState(enquiry);

    // Overlay each product's live price from /product-prices, keyed by
    // productEnquiryHistoryId. This is fetched per-enquiry (one call covers
    // every product line) rather than per-product, since the endpoint
    // already returns the full set for the enquiry. Lines without a
    // matching productEnquiryHistoryId (e.g. couldn't be resolved) keep
    // whatever price mapEnquiryToFormState already set. The value stays
    // fully editable afterwards via the existing updateProductPrice.
    const priceByHistoryId = await fetchProductPrices(enquiryId);
    if (Object.keys(priceByHistoryId).length > 0) {
      formState.products = formState.products.map((product) => {
        if (
          product.productEnquiryHistoryId != null &&
          priceByHistoryId[product.productEnquiryHistoryId] != null
        ) {
          const livePrice = priceByHistoryId[product.productEnquiryHistoryId];
          return {
            ...product,
            price: livePrice,
            _displayInfo: {
              ...product._displayInfo,
              sellingPrice: livePrice,
            },
          };
        }
        return product;
      });
    }

    setEnquiryForm(formState);

    if (!formState.leadId && !formState.clientId) {
      toast.error(
        "Couldn't detect this enquiry's Lead or Client — please select one below before saving.",
      );
    }

    // Populate the Contact Person dropdown for this enquiry's lead/client
    // without disturbing formState.contactPersonId — unlike
    // handleLeadChange/fetchLeadDetails, we must NOT reset it to "the
    // lead's first contact", since the enquiry may already point at a
    // different one.
    try {
      if (formState.leadId) {
        const res = await axiosInstance.get(
          getApiEndpoint(`/leads/${formState.leadId}`),
        );
        setContactPersons(res.data?.data?.contactPersons || []);
      } else if (formState.clientId) {
        const matchingClient = clients.find((c) => c.id === formState.clientId);
        setContactPersons(matchingClient?.contactPersons || []);
      } else {
        setContactPersons([]);
      }
    } catch (err) {
      console.error("Failed to load contact persons for editing:", err);
      setContactPersons([]);
    }

    setNoteImagePairs([]);
    setEditMode(true);
    setEditingEnquiryId(enquiryId);
    closeAddProductsModal();
    setShowCreateForm(true);
  };

  const openAddProductsModal = async (enquiryStageHistoryId) => {
    const stageData = await fetchEnquiryStageHistoryById(enquiryStageHistoryId);

    setAddProductsModal({
      isOpen: true,
      enquiryStageHistoryId: enquiryStageHistoryId,
      enquiryId: stageData?.histories?.[0]?.enquiryId || null,
      currentProducts: stageData?.histories?.[0]?.productEnquiryHistories || [],
    });
  };

  const closeAddProductsModal = () => {
    setAddProductsModal({
      isOpen: false,
      enquiryStageHistoryId: null,
      currentProducts: [],
    });
  };

  const handleClientChange = (selectedClientId) => {
    const clientId = selectedClientId ? Number(selectedClientId) : 0;

    const selectedClient = clients.find((c) => c.id === clientId);

    const firstCP = selectedClient?.contactPersons?.[0]?.id || 0;

    setEnquiryForm((prev) => ({
      ...prev,
      clientId,
      leadId: 0,
      contactPersonId: firstCP,
      clientCompanyName: selectedClient?.companyName || "",
    }));

    setContactPersons(selectedClient?.contactPersons || []);
  };

  const createEnquiryValidation = () => {
    if (!enquiryForm.leadId && !enquiryForm.clientId) {
      toast.error("Please select either a Lead or a Client");
      return false;
    }

    if (
      enquiryForm.leadId > 0 &&
      (!enquiryForm.contactPersonId || enquiryForm.contactPersonId === 0)
    ) {
      toast.error("Please select a Contact Person");
      return false;
    }

    if (enquiryForm.products.length === 0) {
      toast.error("Please add at least one product combination");
      return false;
    }

    for (let i = 0; i < enquiryForm.products.length; i++) {
      const product = enquiryForm.products[i];

      if (!product.productId || product.productId === 0) {
        toast.error(`Please select a product for item #${i + 1}`);
        return false;
      }

      if (!product.combinationId || product.combinationId === 0) {
        toast.error(`Please select a combination for item #${i + 1}`);
        return false;
      }

      if (!product.quantity || product.quantity < 1) {
        toast.error(`Quantity must be at least 1 for item #${i + 1}`);
        return false;
      }
    }

    return true;
  };

  // ── Build the notes string that goes to the backend.
  // Combines the main notes field + any note-image pair notes as bullet points.
  const buildBackendNotes = () => {
    const parts = [];

    if (enquiryForm.notes && enquiryForm.notes.trim()) {
      parts.push(enquiryForm.notes.trim());
    }

    const pairNotes = noteImagePairs
      .map((p) => p.note.trim())
      .filter(Boolean)
      .map((n) => `• ${n}`);

    if (pairNotes.length) {
      parts.push(pairNotes.join("\n"));
    }

    return parts.join("\n\n");
  };

  const createEnquiry = async () => {
    if (!createEnquiryValidation()) return;

    const isEditing = Boolean(editMode && editingEnquiryId);

    try {
      setLoading(true);

      // This "quick lead" shape (blank email/phone/jobTitle/etc.) only makes
      // sense when creating a brand-new enquiry from a bare lead reference.
      // When editing an existing enquiry, the lead already has real data —
      // resending this synthetic object would blank those fields out, so we
      // skip it entirely and just reference the existing leadId instead.
      const leadPayload =
        !isEditing && enquiryForm.leadId
          ? {
              id: enquiryForm.leadId,
              leadSource: "OTHER",
              leadStatus: "AVAILABLE",
              email: "",
              phoneNumber: "",
              companyName: enquiryForm.clientCompanyName || "",
              jobTitle: "",
              industry: "",
              website: "",
              notes: buildBackendNotes(),
              tags: "",
              leadScore: 0,
              assignedToId: user?.empId || null,
              category: "CASH",
            }
          : null;

      const consolidatedProducts = enquiryForm.products.reduce(
        (acc, product) => {
          const key = `${product.productId}-${product.combinationId}`;

          if (acc[key]) {
            acc[key].quantity += parseInt(product.quantity, 10) || 1;
          } else {
            acc[key] = {
              ...product,
              quantity: parseInt(product.quantity, 10) || 1,
            };
          }

          return acc;
        },
        {},
      );

      const productsArray = Object.values(consolidatedProducts);

      // Fields shared by both create and update, built once so the two
      // payload shapes below don't drift apart on the common bits.
      const sharedFields = {
        priority: enquiryForm.priority || "MEDIUM",
        expectedCloseDate: enquiryForm.expectedCloseDate || null,
        discountPercentage:
          enquiryForm.discountPercentage === ""
            ? 0
            : Number(enquiryForm.discountPercentage),
        // Send combined notes (main notes + pair notes as bullets) to backend
        notes: buildBackendNotes(),
        type: enquiryForm.type || "WARM",
      };

      const products = productsArray
        .map((product) => {
          if (!product.productId || product.productId === 0) {
            console.error("Missing productId:", product);
            return null;
          }

          const combinationId = product.combinationId
            ? Number(product.combinationId)
            : product._displayInfo?.combinationId || null;

          if (!combinationId) {
            console.error("Missing combinationId:", product);
            return null;
          }

          const quantity = parseInt(product.quantity, 10);
          if (isNaN(quantity) || quantity < 1) {
            console.error("Invalid quantity for product:", product);
            return null;
          }

          const specifications = Array.isArray(product.specifications)
            ? product.specifications
                .map((spec) => {
                  if (
                    !spec.productSpecificationId ||
                    !spec.productSpecificationValueId
                  ) {
                    console.warn("Invalid specification (missing IDs):", spec);

                    if (product._displayInfo?.specs) {
                      const matchingSpec = product._displayInfo.specs.find(
                        (displaySpec) =>
                          displaySpec.specificationId ===
                          spec.productSpecificationId,
                      );

                      if (matchingSpec) {
                        return {
                          productSpecificationId: Number(
                            matchingSpec.specificationId,
                          ),
                          productSpecificationValueId: Number(
                            matchingSpec.valueId,
                          ),
                        };
                      }
                    }

                    return null;
                  }

                  return {
                    productSpecificationId: Number(spec.productSpecificationId),
                    productSpecificationValueId: Number(
                      spec.productSpecificationValueId,
                    ),
                  };
                })
                .filter(Boolean)
            : [];

          if (specifications.length === 0) {
            console.error("No valid specifications for product:", product);
            return null;
          }

          const topicsRequestDTOList = Array.isArray(
            product.topicsRequestDTOList,
          )
            ? product.topicsRequestDTOList
                .filter((t) => t.topicDetails?.trim() || t.topicId)
                .map((t) => ({
                  ...(t.topicId ? { topicId: Number(t.topicId) } : {}),
                  topicDetails: t.topicDetails || "",
                  // The update schema wants these three on every topic.
                  // There's no dedicated UI for them yet, so default to
                  // the current user / the enquiry's own contact person /
                  // the current stage — override with real values here if
                  // topics ever get their own employee or stage picker.
                  ...(isEditing
                    ? {
                        employeeId: t.employeeId || user?.empId || null,
                        contactPersonId:
                          t.contactPersonId ||
                          enquiryForm.contactPersonId ||
                          null,
                        enquiryStageHistoryId:
                          t.enquiryStageHistoryId ||
                          enquiryForm.currentEnquiryStageHistoryId ||
                          null,
                      }
                    : {}),
                  actionsRequestDTOList: Array.isArray(t.actionsRequestDTOList)
                    ? t.actionsRequestDTOList
                        .filter((a) => a.actions?.trim() || a.actionId)
                        .map((a) => ({
                          ...(a.actionId
                            ? { actionId: Number(a.actionId) }
                            : {}),
                          actions: a.actions || "",
                          status: a.status || "PLANNED",
                          remarks: a.remarks || "",
                        }))
                    : [],
                }))
            : [];

          const finalProduct = {
            // Update-only: lets the backend match this line back to its
            // existing product-enquiry-history row. Omitted on create,
            // where there's nothing to match yet.
            ...(isEditing && product.productEnquiryHistoryId
              ? {
                  productEnquiryHistoryId: Number(
                    product.productEnquiryHistoryId,
                  ),
                }
              : {}),
            productId: Number(product.productId),
            combinationId: combinationId,
            quantity: quantity,
            price: Number(product.price) || 0,
            customPrice: Boolean(product.customPrice),
            status: product.status || "PENDING",
            specifications: specifications,
            topicsRequestDTOList,
          };

          return finalProduct;
        })
        .filter((product) => product && product.productId);

      // The update endpoint (PUT /sales/{enquiryId}) takes a narrower shape
      // than create: no lead/client (those aren't editable here — see
      // lockLeadClient in EnquiryCreateForm) and a flat contactPersonId
      // instead of a nested contactPerson object.
      const payload = isEditing
        ? {
            contactPersonId: enquiryForm.contactPersonId || null,
            ...sharedFields,
            products,
          }
        : {
            lead: leadPayload,
            clientId: enquiryForm.clientId || null,
            contactPerson: enquiryForm.contactPersonId
              ? { id: enquiryForm.contactPersonId }
              : null,
            ...sharedFields,
            products,
          };

      const response = isEditing
        ? await axiosInstance.put(
            getApiEndpoint(`/${editingEnquiryId}`),
            payload,
          )
        : await axiosInstance.post(getApiEndpoint("/enquiries"), payload);

      if (response.status === 200 || response.status === 201) {
        toast.success(
          isEditing
            ? "Enquiry updated successfully!"
            : "Enquiry created successfully!",
        );
        setShowCreateForm(false);
        resetForm();
        fetchStageHistory();
      }
    } catch (err) {
      console.error("Save Enquiry Error:", err);
      console.error("Error details:", err.response?.data);
      toast.error(
        err.response?.data?.message ||
          (isEditing ? "Failed to update enquiry" : "Failed to create enquiry"),
      );
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setEnquiryForm({
      id: 0,
      leadId: 0,
      clientId: 0,
      contactPersonId: 0,
      clientCompanyName: "",
      priority: "HIGH",
      expectedCloseDate: new Date().toISOString().split("T")[0],
      discountPercentage: "",
      notes: "",
      type: "HOT",
      status: "PENDING",
      currentEnquiryStageHistoryId: null,
      products: [],
    });
    setContactPersons([]);
    setSearchQuery("");
    setSelectedCategoryPath([]);
    setEditMode(false);
    setEditingEnquiryId(null);
    setNoteImagePairs([]);
  };

  const handleOpenMOM = (stageHistoryItem) => {
    setMomModal({
      isOpen: true,
      enquiryId: stageHistoryItem.enquiryId,
      enquiryStageHistoryId: stageHistoryItem.id,
    });
  };

  const fetchEnquiryStageHistoryById = async (enquiryStageHistoryId) => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(
        getApiEndpoint(`/enquiry-stage-history/${enquiryStageHistoryId}`),
      );

      if (response.data?.data) {
        return response.data.data;
      }
      return null;
    } catch (err) {
      console.error("Failed to fetch enquiry stage history:", err);
      toast.error("Failed to load enquiry stage history");
      return null;
    } finally {
      setLoading(false);
    }
  };

  // ── NOTE-IMAGE PAIR HELPERS ────────────────────────────────────────────────

  const addNoteImagePair = () => {
    setNoteImagePairs((prev) => [
      ...prev,
      {
        id: `nip-${Date.now()}`,
        note: "",
        imageFile: null,
        imageDataUrl: null,
      },
    ]);
  };

  const updatePairNote = (id, value) => {
    setNoteImagePairs((prev) =>
      prev.map((p) => (p.id === id ? { ...p, note: value } : p)),
    );
  };

  const updatePairImage = (id, file) => {
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      setNoteImagePairs((prev) =>
        prev.map((p) =>
          p.id === id
            ? { ...p, imageFile: file, imageDataUrl: e.target.result }
            : p,
        ),
      );
    };
    reader.readAsDataURL(file);
  };

  const removePairImage = (id) => {
    setNoteImagePairs((prev) =>
      prev.map((p) =>
        p.id === id ? { ...p, imageFile: null, imageDataUrl: null } : p,
      ),
    );
  };

  const removeNoteImagePair = (id) => {
    setNoteImagePairs((prev) => prev.filter((p) => p.id !== id));
  };

  // ── TOPICS & ACTIONS HELPERS ───────────────────────────────────────────────

  const addTopicToProduct = (productIndex) => {
    setEnquiryForm((prev) => {
      const products = prev.products.map((p, i) => {
        if (i !== productIndex) return p;
        return {
          ...p,
          topicsRequestDTOList: [
            ...(p.topicsRequestDTOList || []),
            {
              _id: `topic-${Date.now()}`,
              topicId: "",
              topicDetails: "",
              actionsRequestDTOList: [],
            },
          ],
        };
      });
      return { ...prev, products };
    });
  };

  const updateTopic = (productIndex, topicIndex, field, value) => {
    setEnquiryForm((prev) => {
      const products = prev.products.map((p, i) => {
        if (i !== productIndex) return p;
        const topics = (p.topicsRequestDTOList || []).map((t, ti) =>
          ti === topicIndex ? { ...t, [field]: value } : t,
        );
        return { ...p, topicsRequestDTOList: topics };
      });
      return { ...prev, products };
    });
  };

  const removeTopic = (productIndex, topicIndex) => {
    setEnquiryForm((prev) => {
      const products = prev.products.map((p, i) => {
        if (i !== productIndex) return p;
        return {
          ...p,
          topicsRequestDTOList: (p.topicsRequestDTOList || []).filter(
            (_, ti) => ti !== topicIndex,
          ),
        };
      });
      return { ...prev, products };
    });
  };

  const addActionToTopic = (productIndex, topicIndex) => {
    setEnquiryForm((prev) => {
      const products = prev.products.map((p, i) => {
        if (i !== productIndex) return p;
        const topics = (p.topicsRequestDTOList || []).map((t, ti) => {
          if (ti !== topicIndex) return t;
          return {
            ...t,
            actionsRequestDTOList: [
              ...(t.actionsRequestDTOList || []),
              {
                _id: `action-${Date.now()}`,
                actionId: "",
                actions: "",
                status: "PLANNED",
                remarks: "",
              },
            ],
          };
        });
        return { ...p, topicsRequestDTOList: topics };
      });
      return { ...prev, products };
    });
  };

  const updateAction = (
    productIndex,
    topicIndex,
    actionIndex,
    field,
    value,
  ) => {
    setEnquiryForm((prev) => {
      const products = prev.products.map((p, i) => {
        if (i !== productIndex) return p;
        const topics = (p.topicsRequestDTOList || []).map((t, ti) => {
          if (ti !== topicIndex) return t;
          const actions = (t.actionsRequestDTOList || []).map((a, ai) =>
            ai === actionIndex ? { ...a, [field]: value } : a,
          );
          return { ...t, actionsRequestDTOList: actions };
        });
        return { ...p, topicsRequestDTOList: topics };
      });
      return { ...prev, products };
    });
  };

  const removeAction = (productIndex, topicIndex, actionIndex) => {
    setEnquiryForm((prev) => {
      const products = prev.products.map((p, i) => {
        if (i !== productIndex) return p;
        const topics = (p.topicsRequestDTOList || []).map((t, ti) => {
          if (ti !== topicIndex) return t;
          return {
            ...t,
            actionsRequestDTOList: (t.actionsRequestDTOList || []).filter(
              (_, ai) => ai !== actionIndex,
            ),
          };
        });
        return { ...p, topicsRequestDTOList: topics };
      });
      return { ...prev, products };
    });
  };

  // ── PRODUCT CART HELPERS ──────────────────────────────────────────────────

  const addProductToCart = (productId, combination) => {
    // Custom pricing is now a property of the product/combination itself,
    // as returned by the /products/pricing/combinations/all endpoint —
    // no longer derived from the enquiry stage.
    const requiresCustomPricing = Boolean(combination.customPricing);

    const sellingPrice = calculateSellingPrice(
      combination.costPrice,
      combination.marginPercentage,
    );
    const defaultPrice = sellingPrice;
    const initialPrice = requiresCustomPricing ? defaultPrice : defaultPrice;

    const existingProductIndex = enquiryForm.products.findIndex(
      (product) =>
        product.productId === productId &&
        product.combinationId === combination.combinationId,
    );

    if (existingProductIndex >= 0) {
      setEnquiryForm((prev) => ({
        ...prev,
        products: prev.products.map((product, index) =>
          index === existingProductIndex
            ? {
                ...product,
                quantity: product.quantity + 1,
                price: requiresCustomPricing ? product.price : product.price,
              }
            : product,
        ),
      }));
      toast.success(
        `Quantity increased for ${combination.specificationValues
          .map((spec) => spec.value)
          .join(", ")}`,
      );
    } else {
      const newProduct = {
        productId: productId,
        combinationId: combination.combinationId,
        quantity: 1,
        price: initialPrice,
        taxRate: requiresCustomPricing ? 0 : (combination.taxRate ?? 0),
        customPrice: requiresCustomPricing,
        status: "PENDING",
        specifications: combination.specificationValues.map((spec) => ({
          productSpecificationId: spec.specificationId,
          productSpecificationValueId: spec.valueId,
        })),
        _displayInfo: {
          productName: allProductCombinations.find(
            (p) => p.productId === productId,
          )?.productName,
          specs: combination.specificationValues,
          costPrice: combination.costPrice,
          marginPercentage: combination.marginPercentage,
          sellingPrice: sellingPrice,
          discountPrice: combination.discountPrice,
          requiresCustomPricing: requiresCustomPricing,
          combinationId: combination.combinationId,
          taxRate: combination.taxRate ?? 0,
        },
      };

      setEnquiryForm((prev) => ({
        ...prev,
        products: [...prev.products, newProduct],
      }));

      if (requiresCustomPricing) {
        toast.success("Product added! You can edit the price if needed.");
      } else {
        toast.success("Product combination added!");
      }
    }
  };

  const updateProductQuantity = (index, quantity) => {
    const newQuantity = parseInt(quantity, 10);
    if (isNaN(newQuantity) || newQuantity < 1) {
      toast.error("Quantity must be at least 1");
      return;
    }

    setEnquiryForm((prev) => ({
      ...prev,
      products: prev.products.map((product, i) =>
        i === index ? { ...product, quantity: newQuantity } : product,
      ),
    }));
  };

  const removeProductFromCart = (index) => {
    setEnquiryForm((prev) => ({
      ...prev,
      products: prev.products.filter((_, i) => i !== index),
    }));
    toast.info("Product removed");
  };

  const updateProductPrice = (index, price) => {
    setEnquiryForm((prev) => ({
      ...prev,
      products: prev.products.map((product, i) =>
        i === index ? { ...product, price: Number(price) } : product,
      ),
    }));
  };

  const updateProductTaxRate = (index, rate) => {
    const parsed = parseFloat(rate);
    setEnquiryForm((prev) => ({
      ...prev,
      products: prev.products.map((product, i) =>
        i === index
          ? { ...product, taxRate: isNaN(parsed) ? 0 : parsed }
          : product,
      ),
    }));
  };

  const handleFloatInput = (value) => {
    if (value === "") return "";
    const num = parseFloat(value);
    return isNaN(num) ? "" : num;
  };

  const updateEnquiryStatus = async (
    enquiryId,
    newStatus,
    nextEnquiryStageId,
  ) => {
    try {
      setLoading(true);

      const payload = {
        enquiryStatus: newStatus,
        nextEnquiryStageId: nextEnquiryStageId,
      };

      const response = await axiosInstance.put(
        `${process.env.REACT_APP_SALES}/enquiry-stage-history`,
        payload,
      );

      if (response.data.status || response.status === 200) {
        toast.success(
          `Enquiry status updated to ${newStatus.replace("_", " ")}`,
        );
        return true;
      }
    } catch (err) {
      console.log(err);
      return false;
    } finally {
      setLoading(false);
    }
  };

  const updateProductFulfillmentStatus = async (
    productEnquiryHistoryId,
    status,
  ) => {
    try {
      setLoading(true);

      if (Array.isArray(productEnquiryHistoryId)) {
        const promises = [];

        productEnquiryHistoryId.forEach((product) => {
          // Price/quantity changes go to the custom-price endpoint, which
          // also updates fulfillmentStatus as part of the same call. When
          // that's the case, we skip the plain status endpoint below so we
          // don't fire two PUTs for the same fulfillment-status update.
          const usingCustomPriceEndpoint =
            product.priceChanged || product.quantityChanged;

          // Fulfillment status changes go to the original status endpoint —
          // but only when price/quantity aren't also changing, since the
          // custom-price endpoint already carries fulfillmentStatus.
          if ((product.statusChanged ?? true) && !usingCustomPriceEndpoint) {
            promises.push(
              axiosInstance.put(
                getApiEndpoint(`/enquiry/product-history/status/${product.id}`),
                {
                  fulfillmentStatus:
                    product.fulfillmentStatus || product.status,
                },
              ),
            );
          }

          // Custom price/quantity changes go to the custom-price endpoint,
          // carrying the full body it expects. Note: this endpoint's
          // `status` field is a separate custom-price approval enum
          // (REJECTED / PENDING / APPROVED) — NOT the same as
          // fulfillmentStatus — so it's sent as "PENDING" rather than
          // reusing the fulfillment value.
          if (usingCustomPriceEndpoint) {
            promises.push(
              axiosInstance.put(
                getApiEndpoint(
                  `/enquiry/product-history/product-enquiry-history/status/product/${product.id}`,
                ),
                {
                  fulfillmentStatus:
                    product.fulfillmentStatus || product.status,
                  status: "PENDING",
                  quantity: product.quantity,
                  customPrice: !!product.customPrice,
                  price: product.price ?? 0,
                },
              ),
            );
          }
        });

        const responses = await Promise.all(promises);

        const allSuccess = responses.every(
          (response) => response.data?.status || response.status === 200,
        );

        if (allSuccess) {
          toast.success(
            `${productEnquiryHistoryId.length} product status(es) updated successfully!`,
          );
          fetchStageHistory();
          return true;
        }
        return false;
      } else {
        const response = await axiosInstance.put(
          getApiEndpoint(
            `/enquiry/product-history/status/${productEnquiryHistoryId}`,
          ),
          {
            fulfillmentStatus: status,
          },
        );

        if (response.data?.status || response.status === 200) {
          toast.success(`Product status updated to ${status}`);
          fetchStageHistory();
          return true;
        }
      }
    } catch (err) {
      console.error("Failed to update product status:", err);
      toast.error("Failed to update product status");
      return false;
    } finally {
      setLoading(false);
    }
  };

  const updateEnquiryStage = async (
    enquiryStageHistoryId,
    nextEnquiryStageId,
  ) => {
    try {
      const payload = {
        enquiryStageHistoryId: enquiryStageHistoryId,
        nextEnquiryStageId: nextEnquiryStageId,
      };

      const response = await axiosInstance.put(
        getApiEndpoint("/enquiry-stage-history"),
        payload,
      );

      if (response.data.status || response.status === 200) {
        toast.success("Enquiry moved to next stage successfully!");
        fetchStageHistory();
        return true;
      }
    } catch (err) {
      console.error(err);
      return false;
    }
  };

  const getFilteredProducts = () => {
    if (!searchQuery.trim()) return allProductCombinations;

    const query = searchQuery.toLowerCase().trim();

    return allProductCombinations
      .map((product) => {
        const matchingCombinations = product.combinations?.filter(
          (combination) =>
            combination.specificationValues?.some(
              (spec) =>
                spec.value?.toLowerCase().includes(query) ||
                spec.specificationName?.toLowerCase().includes(query),
            ),
        );

        if (
          product.productName?.toLowerCase().includes(query) ||
          matchingCombinations?.length
        ) {
          return {
            ...product,
            combinations: product.productName?.toLowerCase().includes(query)
              ? product.combinations
              : matchingCombinations,
          };
        }

        return null;
      })
      .filter(Boolean);
  };

  return (
    <div className="enquiries-page">
      {loading && <LoadingScreen />}
      {/* Create Enquiry Form */}
      {showCreateForm && (
        <EnquiryCreateForm
          user={user}
          loading={loading}
          editMode={editMode}
          editingEnquiryId={editingEnquiryId}
          enquiryForm={enquiryForm}
          setEnquiryForm={setEnquiryForm}
          leads={leads}
          clients={clients}
          contactPersons={contactPersons}
          handleLeadChange={handleLeadChange}
          handleClientChange={handleClientChange}
          setShowQuickAddLead={setShowQuickAddLead}
          handleFloatInput={handleFloatInput}
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          getFilteredProducts={getFilteredProducts}
          getCategoryLevels={getCategoryLevels}
          selectedCategoryPath={selectedCategoryPath}
          setSelectedCategoryPath={setSelectedCategoryPath}
          setAllProductCombinations={setAllProductCombinations}
          productCatalogRef={productCatalogRef}
          addProductToCart={addProductToCart}
          calculateSellingPrice={calculateSellingPrice}
          updateProductQuantity={updateProductQuantity}
          removeProductFromCart={removeProductFromCart}
          updateProductPrice={updateProductPrice}
          updateProductTaxRate={updateProductTaxRate}
          addTopicToProduct={addTopicToProduct}
          updateTopic={updateTopic}
          removeTopic={removeTopic}
          addActionToTopic={addActionToTopic}
          updateAction={updateAction}
          removeAction={removeAction}
          noteImagePairs={noteImagePairs}
          addNoteImagePair={addNoteImagePair}
          updatePairNote={updatePairNote}
          updatePairImage={updatePairImage}
          removePairImage={removePairImage}
          removeNoteImagePair={removeNoteImagePair}
          setShowCreateForm={setShowCreateForm}
          resetForm={resetForm}
          createEnquiry={createEnquiry}
        />
      )}
      {momModal.isOpen && (
        <div className="modal-overlay">
          <MinutesOfMeetingManager
            enquiryId={momModal.enquiryId}
            enquiryStageHistoryId={momModal.enquiryStageHistoryId}
            axiosInstance={axiosInstance}
            getApiEndpoint={getApiEndpoint}
            onClose={() =>
              setMomModal({
                isOpen: false,
                enquiryId: null,
                enquiryStageHistoryId: null,
              })
            }
          />
        </div>
      )}
      <div className="stage-history-container">
        <EnquiryStageHistory
          onOpenMOM={handleOpenMOM}
          data={stageHistory}
          stageSummary={stageSummary}
          loading={loading}
          enquiryStages={enquiryStages}
          onRefresh={fetchStageHistory}
          onStageUpdate={updateEnquiryStage}
          onChecklistComplete={completeChecklistItem}
          currentUserId={user?.id}
          onChecklistQuickComplete={completeChecklistItem}
          onCreateNew={() => setShowCreateForm(!showCreateForm)}
          showCreateForm={showCreateForm}
          onStatusUpdate={updateEnquiryStatus}
          isWebSocketConnected={isWebSocketConnected}
          isTeamView={false}
          onAddProducts={openAddProductsModal}
          getApiEndpoint={getApiEndpoint}
          axiosInstance={axiosInstance}
          // Pagination is per-column now (the backend requires a stageId
          // and paginates that stage's records independently), so there's
          // no single "end of list" for the whole board. Each column needs
          // to know its own hasMore/loading state and call
          // onLoadMoreForStage(stageId) — typically from an onScroll handler
          // or IntersectionObserver on that column's own scroll container —
          // as the user nears the bottom of that specific column.
          paginationByStage={paginationByStage}
          loadingStageIds={loadingStageIds}
          onLoadMoreForStage={fetchMoreStageHistoryForStage}
          leadOptions={leads}
          clientOptions={clients}
          onServerFilter={fetchStageHistoryForFilter}
        />
      </div>

      <LeadQuickAddModal
        isOpen={showQuickAddLead}
        onClose={() => setShowQuickAddLead(false)}
        onLeadCreated={handleLeadCreated}
        axiosInstance={axiosInstance}
        getApiEndpoint={getApiEndpoint}
      />

      <AddProductsModal
        isOpen={addProductsModal.isOpen}
        onClose={closeAddProductsModal}
        enquiryId={addProductsModal.enquiryId}
        onStatusUpdate={updateProductFulfillmentStatus}
        currentProducts={addProductsModal.currentProducts}
        onEditEnquiry={handleEditEnquiry}
      />
    </div>
  );
};

export default Enquiries;
